import { useState, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { Box, Grid, Typography, useTheme } from '@mui/material'
import { ActivityIndicator } from 'components/Shared/ActivityIndicator'
import { Page } from 'components/Shared/Page'
import { PageHeader } from 'components/Shared/PageHeader'
import { SectionHeader } from 'components/SectionHeader'
import { SimpleImageCard } from 'components/ImageCard/SimpleImageCard'
import { formatDateTime } from 'utils/util'
import { JJBadgesEdit } from './JJBadgesEdit'
import { useGetBadgeRewardById } from 'hooks/api/Contests/BadgeReward/useGetBadgeRewardsById'
import { useGetAllJJBadges } from 'hooks/api/Contests/BadgeReward/useGetAllJJBadges'
import { BadgesUploadImage } from '../BadgesUploadImage'
import { useGetPatronBadgeCountsByBadgeId } from 'hooks/api/useGetPatronBadgeCountsByBadgeId'
import {
  badgeRewardTypeHasAccessToControl,
  getBadgeRewardTypeName,
} from 'utils/badgeRewardsUtils'

export const JJBadgesDetailPage = () => {
  const params = useParams()
  const theme = useTheme()
  const id = Number(useParams().id)
  const [isImageModalOpen, setIsImageModalOpen] = useState(false)
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false)
  const [imageType, setImageType] = useState<'notEarned' | 'earned'>(
    'notEarned'
  )

  const getPatronBadgeCountQuery = useGetPatronBadgeCountsByBadgeId({
    id,
  })
  const totalCount = getPatronBadgeCountQuery.data?.totalCount
  const uniqueCount = getPatronBadgeCountQuery.data?.uniqueCount

  const allJJBadgesQuery = useGetAllJJBadges()
  const jjBadges = useMemo(() => {
    // remove the badge reward that is currently being edited from allJJBadges to avoid having the option to select itself as a dependent badge reward
    const jjBadgesData = allJJBadgesQuery.data ?? []
    const filteredJJBadges = jjBadgesData.filter(
      (br) => br.id !== Number(params.id)
    )

    return filteredJJBadges.reduce<Record<number, string>>((acc, br) => {
      acc[br.id as number] = br.name
      return acc
    }, {})
  }, [allJJBadgesQuery.data, params.id])

  const toggleImageModalIsOpen = async (
    type: 'notEarned' | 'earned' = 'notEarned'
  ) => {
    setImageType(type)
    setIsImageModalOpen(!isImageModalOpen)
  }

  const toggleDetailsModalIsOpen = async () => {
    setIsDetailsModalOpen(!isDetailsModalOpen)
  }

  const badgeRewardQuery = useGetBadgeRewardById({
    badgeRewardId: Number(params.id),
  })

  if (badgeRewardQuery.isPending) {
    return <ActivityIndicator />
  }
  if (badgeRewardQuery.isError) {
    return <Typography>There was an error</Typography>
  }

  const badgeReward = badgeRewardQuery.data

  if (!badgeReward?.id) {
    return <Typography>Badge reward not found</Typography>
  }

  return (
    <Page
      header={
        <PageHeader
          title={badgeReward?.name ?? ''}
          backText="J&J Badges"
          backPath="/Contests/JJBadges"
          isSecondary={true}
        />
      }
    >
      <Box className="gap-12 flex flex-col">
        <Box>
          <SectionHeader
            title="Badge Details"
            onClickButton={toggleDetailsModalIsOpen}
          />
          <Box
            className="flex-1"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              rowGap: '16px',
              borderWidth: 1,
              borderColor: 'grey.300',
              borderRadius: 2,
              p: '24px',
            }}
          >
            <Grid className="flex-1">
              <div className="flex flex-col">
                <div className="pb-1 grid md:grid-cols-5">
                  <Grid item md={2}>
                    <Typography variant="body2" mb={1} color="text.secondary">
                      Type
                    </Typography>
                    <Typography variant="body-1" mb={2}>
                      {getBadgeRewardTypeName(badgeReward?.type)}
                    </Typography>
                  </Grid>
                  {badgeRewardTypeHasAccessToControl(
                    badgeReward?.type,
                    'StartDateTime'
                  ) && (
                    <Grid item md={2}>
                      <Typography variant="body2" mb={1} color="text.secondary">
                        Start Date
                      </Typography>
                      <Typography variant="body1" mb={2}>
                        {badgeReward?.startDate
                          ? formatDateTime(badgeReward?.startDate, 'P p')
                          : 'None'}
                      </Typography>
                    </Grid>
                  )}
                  {badgeRewardTypeHasAccessToControl(
                    badgeReward?.type,
                    'EndDateTime'
                  ) && (
                    <Grid item md={2}>
                      <Typography variant="body2" mb={1} color="text.secondary">
                        End Date
                      </Typography>
                      <Typography variant="body1" mb={2}>
                        {badgeReward?.endDate
                          ? formatDateTime(badgeReward?.endDate, 'P p')
                          : 'None'}
                      </Typography>
                    </Grid>
                  )}
                  {badgeRewardTypeHasAccessToControl(
                    badgeReward?.type,
                    'Schedule'
                  ) && (
                    <Grid item sm={8} md={3} lg={3}>
                      <Box>
                        <Typography
                          variant="body2"
                          mb={1}
                          color="text.secondary"
                        >
                          Scheduled Days
                        </Typography>
                        <Typography variant="body1">
                          {badgeReward?.badgeRewardSchedules &&
                          badgeReward?.badgeRewardSchedules?.length > 0
                            ? 'Yes'
                            : 'No'}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  {badgeRewardTypeHasAccessToControl(
                    badgeReward?.type,
                    'Count'
                  ) && (
                    <Grid item sm={8} md={3} lg={3}>
                      <Box>
                        <Typography
                          variant="body2"
                          mb={1}
                          color="text.secondary"
                        >
                          {getBadgeRewardTypeName(badgeReward?.type)} Count
                        </Typography>
                        <Typography variant="body1">
                          {badgeReward?.count === 0 ? '' : badgeReward?.count}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </div>
                <div className="pb-1 grid md:grid-cols-5">
                  <div>
                    <Typography variant="body-3">Description</Typography>
                    <Typography variant="body-1">
                      {badgeReward?.description ? badgeReward.description : '-'}
                    </Typography>
                  </div>
                  {badgeRewardTypeHasAccessToControl(
                    badgeReward?.type,
                    'Renewable'
                  ) && (
                    <Grid item md={2}>
                      <Typography variant="body2" pb={1} color="text.secondary">
                        Renewable
                      </Typography>
                      <Typography variant="body-1">
                        {(() => {
                          switch (badgeReward?.renewableType ?? '') {
                            case 'Daily':
                              return 'Daily'
                            case 'Weekly':
                              return 'Weekly'
                            case 'Monthly':
                              return 'Monthly'
                            case 'Quarterly':
                              return 'Quarterly'
                            case 'Yearly':
                              return 'Yearly'
                            case 'CheckInStreak':
                              return 'Check-In Streak'
                            default:
                              return 'No'
                          }
                        })()}
                      </Typography>
                    </Grid>
                  )}
                  {badgeRewardTypeHasAccessToControl(
                    badgeReward?.type,
                    'DependentOnBadge'
                  ) && (
                    <Grid item md={2}>
                      <Typography variant="body2" pb={1} color="text.secondary">
                        Depends On Badge Id
                      </Typography>
                      <Typography variant="body-1">
                        {badgeReward?.dependentBadgeRewardId === 0
                          ? ''
                          : badgeReward?.dependentBadgeRewardId}
                      </Typography>
                    </Grid>
                  )}

                  {badgeRewardTypeHasAccessToControl(
                    badgeReward?.type,
                    'LimitCheckins'
                  ) && (
                    <Grid item md={2}>
                      <Typography variant="body2" pb={1} color="text.secondary">
                        Limit to 1 Check-in Per Day
                      </Typography>
                      <Typography variant="body-1">
                        {badgeReward?.limitCheckins === false ? 'No' : 'Yes'}
                      </Typography>
                    </Grid>
                  )}

                  {badgeRewardTypeHasAccessToControl(
                    badgeReward?.type,
                    'JJPointsToAward'
                  ) && (
                    <Grid item md={2}>
                      <Typography variant="body2" pb={1} color="text.secondary">
                        JJ Points to award
                      </Typography>
                      <Typography variant="body-1">
                        {badgeReward?.jjPointsAwarded === 0
                          ? ''
                          : badgeReward?.jjPointsAwarded}
                      </Typography>
                    </Grid>
                  )}
                </div>
              </div>
            </Grid>
          </Box>
        </Box>

        {/* Bage reports section */}
        <Box className="pt-4">
          <Box>
            <SectionHeader title="Badge Reports" showButton={false} />
            <Box
              border={{ md: `1px solid ${theme.palette.grey[200]}` }}
              borderRadius={'5px'}
              p={{ xs: 0, sm: 3 }}
            >
              <Grid
                container
                className="flex flex-row flex-wrap"
                columnGap={15}
              >
                <Grid item md={2}>
                  <Typography variant="body2" pb={1} color="text.secondary">
                    Total Count
                  </Typography>
                  <Typography variant="body1" pb={2}>
                    {totalCount}
                  </Typography>
                </Grid>
                <Grid item md={2}>
                  <Typography variant="body2" pb={1} color="text.secondary">
                    Unique Count
                  </Typography>
                  <Typography variant="body1" pb={2}>
                    {uniqueCount}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Box>

        {/* Image section */}
        <Box>
          <SectionHeader
            title="Not Earned Image"
            onClickButton={async () =>
              await toggleImageModalIsOpen('notEarned')
            }
            buttonText="Add Image"
          />

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              rowGap: '16px',
              borderWidth: 1,
              borderColor: 'grey.300',
              borderRadius: 2,
              px: '16px',
              py: '16px',
              mt: '12px',
            }}
          >
            <Grid container>
              <Grid
                item
                xs={12}
                sm={12}
                md={12}
                sx={{ pt: { xs: '24px', sm: 0 } }}
              >
                <Typography variant="body-3" className="w-full">
                  Thumbnail Image
                </Typography>
                <SimpleImageCard
                  imageSource={badgeReward?.imageNotEarned}
                  sx={{ maxWidth: '358px', maxHeight: '358px' }}
                />
              </Grid>
            </Grid>
          </Box>
        </Box>

        {/* Image section */}
        <Box>
          <SectionHeader
            title="Earned Image"
            onClickButton={async () => await toggleImageModalIsOpen('earned')}
            buttonText="Add Image"
          />

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              rowGap: '16px',
              borderWidth: 1,
              borderColor: 'grey.300',
              borderRadius: 2,
              px: '16px',
              py: '16px',
              mt: '12px',
            }}
          >
            <Grid container>
              <Grid
                item
                xs={12}
                sm={12}
                md={12}
                sx={{ pt: { xs: '24px', sm: 0 } }}
              >
                <Typography variant="body-3" className="w-full">
                  Thumbnail Image
                </Typography>
                <SimpleImageCard
                  imageSource={badgeReward?.imageEarned}
                  sx={{ maxWidth: '358px', maxHeight: '358px' }}
                />
              </Grid>
            </Grid>
          </Box>
        </Box>

        <JJBadgesEdit
          isModalOpen={isDetailsModalOpen}
          toggleModalIsOpen={toggleDetailsModalIsOpen}
          badgeReward={badgeReward}
          badgeRewards={jjBadges}
        />

        <BadgesUploadImage
          isModalOpen={isImageModalOpen}
          toggleModalIsOpen={toggleImageModalIsOpen}
          badgeReward={badgeReward}
          imageType={imageType}
        />
      </Box>
    </Page>
  )
}
